import { useEffect } from "react";
import { useLoginContext } from "../../../App";
import { areasRelationToFws } from "../../../services/b2b-frameworks-apiService";
import useB2BTranslation from "../../b2b-common/hooks/useB2BTranslation";
import { useContentContext } from "../../layout/Layout";
import { showNotification } from "../../b2b-common/Notification";
import { useQuery } from "@tanstack/react-query";

const fetchAreasFrameworks = async (frameworkSuite, planAreaDTOs, accessToken) => {
    const fileteredPlanAreaDTOs = planAreaDTOs.filter(planAreaDTO => (planAreaDTO.planAreaId != null && planAreaDTO.planUUID != null));
    const response = await areasRelationToFws(frameworkSuite, fileteredPlanAreaDTOs, true, accessToken);
    return response;
}

export const getFilteredFrameworks = async (accessToken, frameworkSuite, planAreaDTOs, productAcronyms, acronym, suitesAndFwsFilter) => {
    const filter = suitesAndFwsFilter?.find(f => f.frameworkSuite === frameworkSuite);
    if (suitesAndFwsFilter === null || filter) {
        if (productAcronyms.includes(acronym)) {
            const result = await fetchAreasFrameworks(frameworkSuite, planAreaDTOs, accessToken);
            // Sort level1sDTOs and level2sDTOs by their levelN_ItemId
            result.forEach(fw => {
                fw.level1sDTOs.sort((a, b) => a.levelN_ItemId - b.levelN_ItemId);
            });
            // Filter the result if filter.fwIds is provided
            let filteredResult = result;
            if (filter?.fwIds?.length) {
                filteredResult = result.filter(fw => filter.fwIds.includes(fw.frameworkDTO.frameworkId));
            }
            // Return the sorted and filtered result
            return filteredResult;
        }
    }
    return [];
};

const useAreasFrameworks = (planAreaDTOs, suitesAndFwsFilter = null) => {

    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-frameworks');

    const { accessToken, productAcronyms } = loginData;

    const { data: genericFwsRelated, error: genericError } = useQuery({
        queryKey: ['genericFwsRelated-areas', planAreaDTOs],
        queryFn: () => getFilteredFrameworks(accessToken, 'generic', planAreaDTOs, productAcronyms, 'B2B_BASE', suitesAndFwsFilter),
        enabled: suitesAndFwsFilter?.some(filter => filter.frameworkSuite === 'generic'),
    });

    const { data: iotFwsRelated, error: iotError } = useQuery({
        queryKey: ['iotFwsRelated-areas', planAreaDTOs],
        queryFn: () => getFilteredFrameworks(accessToken, 'iot', planAreaDTOs, productAcronyms, 'B2B_IOT_FW', suitesAndFwsFilter),
        enabled: suitesAndFwsFilter?.some(filter => filter.frameworkSuite === 'iot'),
    });

    const { data: verticalFwsRelated, error: verticalError } = useQuery({
        queryKey: ['verticalFwsRelated-areas', planAreaDTOs],
        queryFn: () => getFilteredFrameworks(accessToken, 'vertical', planAreaDTOs, productAcronyms, 'B2B_VERTICAL_FW', suitesAndFwsFilter),
        enabled: suitesAndFwsFilter?.some(filter => filter.frameworkSuite === 'vertical'),
    });

    const { data: partnerFwsRelated, error: partnerError } = useQuery({
        queryKey: ['partnerFwsRelated-areas', planAreaDTOs],
        queryFn: () => getFilteredFrameworks(accessToken, 'partner', planAreaDTOs, productAcronyms, 'B2B_PARTNER_FW', suitesAndFwsFilter),
        enabled: suitesAndFwsFilter?.some(filter => filter.frameworkSuite === 'partner'),
    });

    const { data: softwareFwsRelated, error: softwareError } = useQuery({
        queryKey: ['softwareFwsRelated-areas', planAreaDTOs],
        queryFn: () => getFilteredFrameworks(accessToken, 'software', planAreaDTOs, productAcronyms, 'B2B_SOFTWARE_FW', suitesAndFwsFilter),
        enabled: suitesAndFwsFilter?.some(filter => filter.frameworkSuite === 'software'),
    });

    useEffect(() => {
        if (genericError || iotError || verticalError || softwareError || partnerError) {
            showNotification(setNotification, t('Error fetching frameworks: ') +
                (genericError || iotError || verticalError || softwareError || partnerError).message,
                'error', 5000);
        }
    }, [genericError, iotError, verticalError, softwareError, partnerError]);

    return { genericFwsRelated, iotFwsRelated, verticalFwsRelated, partnerFwsRelated, softwareFwsRelated };

}

export default useAreasFrameworks;